import { SpeechRecognitionLanguage } from '../../constants/audio.ts';
import {
  SpeechRecognitionCallbacks,
  SpeechRecognitionErrorType,
  SpeechRecognitionOptions,
  SpeechRecognitionState,
  SpeechRecognitionResult,
  AudioRecordingOptions,
  AudioRecordingSuccessResult,
  AudioRecordingResult,
} from '../../types/audio.types.ts';

import { getBrowserFeatureCheckUtil } from '../browser-feature-check.util.ts';
import { getSentryUtil } from '../monitoring/sentry.util.ts';

import { getAudioRecordingUtil } from './audio-recording.util.ts';

const RECOGNITION_STOP_TIMEOUT = 1000;

export class SpeechRecognitionUtil {
  private recognition: SpeechRecognition | null = null;
  private audioRecordingUtil = getAudioRecordingUtil();

  private state: SpeechRecognitionState = {
    isListening: false,
    isFinal: false,
    transcript: '',
    interimTranscript: '',
  };

  private options: Required<SpeechRecognitionOptions> = {
    language: SpeechRecognitionLanguage.ENGLISH,
    interimResults: true,
    maxAlternatives: 1,
  };

  private static _instance: SpeechRecognitionUtil;

  static get instance(): SpeechRecognitionUtil {
    if (!SpeechRecognitionUtil._instance) {
      SpeechRecognitionUtil._instance = new SpeechRecognitionUtil();
    }
    return SpeechRecognitionUtil._instance;
  }

  private constructor() {
    if (!getBrowserFeatureCheckUtil().supportsSpeechRecognition()) {
      this.handleError(SpeechRecognitionErrorType.NOT_SUPPORTED);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
  }

  async recognizeSpeech(
    options?: AudioRecordingOptions,
    callbacks?: SpeechRecognitionCallbacks
  ): Promise<SpeechRecognitionResult> {
    if (this.state.isListening) {
      return Promise.reject(new Error('Speech recognition is already in progress'));
    }

    return this.handleRecognition(options, callbacks);
  }

  async stopRecognizingSpeech(): Promise<SpeechRecognitionResult> {
    if (!this.recognition || !this.state.isListening) {
      return {
        error: SpeechRecognitionErrorType.NOT_STARTED,
        success: false,
      };
    }

    this.recognition.stop();
    return this.handleRecognitionEnd();
  }

  private async handleRecognitionStart(options?: AudioRecordingOptions): Promise<AudioRecordingResult> {
    const recordingPromise = this.audioRecordingUtil.record({ maxDuration: 60, ...options });
    this.state.isListening = true;
    this.state.error = undefined;
    this.state.isFinal = false;
    this.state.transcript = '';
    this.state.interimTranscript = '';
    return await recordingPromise;
  }

  private async handleRecognitionEnd(): Promise<SpeechRecognitionResult> {
    const recordingResult = await this.audioRecordingUtil.stopRecording();
    if (!recordingResult.success) {
      this.handleError(SpeechRecognitionErrorType.RECORDING_ERROR);
      return {
        error: SpeechRecognitionErrorType.RECORDING_ERROR,
        success: false,
      };
    }
    return this.finalizeRecognition(recordingResult);
  }

  private finalizeRecognition(recordingResult: AudioRecordingSuccessResult): SpeechRecognitionResult {
    this.state.audioBlobs = recordingResult.audioBlobs;
    this.state.recordingKey = recordingResult.recordingKey;
    this.state.isListening = false;
    this.state.isFinal = true;

    return {
      transcript: this.state.transcript,
      audioBlobs: recordingResult.audioBlobs,
      recordingKey: recordingResult.recordingKey,
      isFinal: true,
      success: true,
    };
  }

  private handleRecognitionEvent(event: SpeechRecognitionEvent): void {
    const result = event.results[event.resultIndex];

    const interimTranscript = result.isFinal ? '' : result[0].transcript;
    const finalTranscript = this.state.transcript + (result.isFinal ? result[0].transcript : '');

    this.state.isFinal = result.isFinal;
    this.state.transcript = finalTranscript;
    this.state.interimTranscript = interimTranscript;
  }

  private handleRecognition(
    options?: AudioRecordingOptions,
    callbacks?: SpeechRecognitionCallbacks
  ): Promise<SpeechRecognitionResult> {
    return new Promise((resolve) => {
      if (!this.recognition)
        return resolve({
          error: SpeechRecognitionErrorType.NOT_SUPPORTED,
          success: false,
        });

      if (this.audioRecordingUtil.isBusy)
        return resolve({
          error: SpeechRecognitionErrorType.RECORDING_IN_PROGRESS,
          success: false,
        });

      try {
        this.recognition.continuous = true;
        this.recognition.interimResults = this.options.interimResults;
        this.recognition.maxAlternatives = this.options.maxAlternatives;
        this.recognition.lang = this.options.language;

        this.recognition.onstart = async (): Promise<void> => {
          const recordingResult = await this.handleRecognitionStart(options);
          if (!recordingResult.success) {
            this.handleError(SpeechRecognitionErrorType.RECORDING_ERROR);
            resolve({
              error: SpeechRecognitionErrorType.RECORDING_ERROR,
              success: false,
            });
            return;
          }
          this.recognition?.stop();
          const finalResult = this.finalizeRecognition(recordingResult);
          callbacks?.onFinalResult?.(finalResult);

          // Wait for the recognition to stop
          // Unfortunatly, the recognition.stop() method doesn't stop immediately,
          // so we need to wait for a short period of time
          // before resolving the promise
          setTimeout(() => {
            resolve(finalResult);
          }, RECOGNITION_STOP_TIMEOUT);
        };

        this.recognition.onend = async (): Promise<void> => {
          const finalResult = await this.handleRecognitionEnd();
          callbacks?.onFinalResult?.(finalResult);
          resolve(finalResult);
        };

        this.recognition.onresult = (event: SpeechRecognitionEvent): void => {
          this.handleRecognitionEvent(event);
          if (this.state.isFinal) {
            this.recognition?.stop();
          } else {
            callbacks?.onInterimResult?.({
              isFinal: false,
              transcript: this.state.interimTranscript,
              success: true,
            });
          }
        };

        this.recognition.onerror = (event: SpeechRecognitionErrorEvent): void => {
          if (event.error === 'not-allowed') {
            this.handleError(SpeechRecognitionErrorType.PERMISSION_DENIED);
          } else {
            this.handleError(SpeechRecognitionErrorType.RECOGNITION_ERROR);
          }
        };

        this.recognition.start();
      } catch (error) {
        this.handleError(SpeechRecognitionErrorType.INITIALIZATION_ERROR, error as Error);
        resolve({
          error: SpeechRecognitionErrorType.INITIALIZATION_ERROR,
          success: false,
        });
      }
    });
  }

  private handleError(type: SpeechRecognitionErrorType, error?: Error): void {
    getSentryUtil().trackError(error ?? new Error(type), { type });
    this.state.error = error;
    this.stopRecognizingSpeech();
  }
}

export const getSpeechRecognitionUtil = (): SpeechRecognitionUtil => SpeechRecognitionUtil.instance;
